import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { useNavigate, useParams } from "react-router-dom";
import useToast from "../../../../components/common/useToast";
import apiFunctions from "../../../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";
import { VideoEmbed } from "../../../../components/homeBuilder/BuilderSections";
import { Skeleton } from "primereact/skeleton";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const isValidLink = (link: string) => /^https?:\/\//i.test(link.trim());

const LiveVideoCreate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();

    const [loading, setLoading] = useState(!!id);
    const [saving, setSaving] = useState(false);

    const [formTitle, setFormTitle] = useState("");
    const [formLink, setFormLink] = useState("");

    useEffect(() => {
        if (!id) return;

        (async () => {
            try {
                setLoading(true);
                const response = await apiFunctions.get(`/livevideo/${id}`);
                if (response?.data?.success) {
                    setFormTitle(response.data.data.title || "");
                    setFormLink(response.data.data.link || "");
                } else {
                    showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Failed to load live video", life: 3000 });
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleBack = () => {
        navigate('/admin/livevideo');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formLink.trim()) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please enter a link", life: 3000 });
            return;
        }
        if (!isValidLink(formLink)) {
            showToast({ severity: "warn", summary: "Warning", detail: "Link must start with http:// or https://", life: 3000 });
            return;
        }

        try {
            setSaving(true);
            const payload = { title: formTitle.trim(), link: formLink.trim() };

            const response = id
                ? await apiFunctions.patch(`/livevideo/${id}`, payload)
                : await apiFunctions.post("/livevideo", payload);

            if (response?.data?.success) {
                showToast({
                    severity: "success",
                    summary: "Success",
                    detail: response.data.message,
                    life: 3000,
                });
                navigate("/admin/livevideo");
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response?.data?.message || "Something went wrong",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                {id ? (<h3>Edit Live Video</h3>) : (<h3>Add Live Video</h3>)}
            </div>
            <form onSubmit={handleSubmit}>
                <Card className="grid form-layout">
                    {loading ? (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="60px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="60px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>

                            <div className="grid grid-cols-12 gap-4" style={{ marginTop: "20px" }}>
                                <div className="col-span-12">
                                    <Skeleton width="70px" height="0.9rem" className="mb-2" />
                                    <Skeleton width="100%" height="360px" borderRadius="12px" />
                                </div>
                            </div>

                            <div style={{ marginTop: "24px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" }}>
                                <Skeleton width="140px" height="40px" />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Title</label>
                                    <PrimeInputText
                                        type="text"
                                        value={formTitle}
                                        onChange={(e) => setFormTitle(e.target.value)}
                                        placeholder="Enter a title (optional)"
                                        className="w-full mt-1"
                                        autoFocus
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Link *</label>
                                    <PrimeInputText
                                        type="text"
                                        value={formLink}
                                        onChange={(e) => setFormLink(e.target.value)}
                                        placeholder="https://..."
                                        className="w-full mt-1"
                                    />
                                </div>
                            </div>

                            {isValidLink(formLink) && (
                                <div className="grid grid-cols-12 gap-4" style={{ marginTop: "20px" }}>
                                    <div className="col-span-12">
                                        <label className="form-label">Preview</label>
                                        <div
                                            className="mt-2"
                                            style={{
                                                width: "100%",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                padding: "16px",
                                                background: "#f9fafb",
                                            }}
                                        >
                                            <VideoEmbed link={formLink.trim()} title={formTitle.trim() || "Live Video"} />
                                            <p className="text-xs m-0 mt-2" style={{ color: "#9ca3af" }}>
                                                This is how the video will appear on the live site.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div
                                className="flex justify-end gap-2"
                                style={{ width: "100%", marginTop: "24px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" }}
                            >
                                <Button
                                    type="button"
                                    label="Cancel"
                                    severity="secondary"
                                    onClick={handleBack}
                                    disabled={saving}
                                />
                                <Button
                                    label={id ? "Update" : "Add"}
                                    className="prime-button btn-login"
                                    loading={saving}
                                    type="submit"
                                />
                            </div>
                        </>
                    )}
                </Card>
            </form>
        </>
    );
};

export default LiveVideoCreate;
